import { useEffect, useMemo, useState } from "react";
import {
  extractDominantColor,
  getCategoryAccent,
  hexToRgba,
  darkenHex,
} from "../services/utility/BrandColorExtractor";

interface UseBusinessBrandColorResult {
  /** Primary accent — badges, active states, icon fills, gradient start. */
  accent: string;
  /** A darker partner shade — gradient end, hover states. */
  accentDark: string;
  /** Soft translucent tint of the accent — subtle backgrounds/scrims. */
  tint: string;
  /** True once the logo has actually been sampled (vs. still on the category default). */
  isDerived: boolean;
}

/**
 * Resolves the brand accent color for a business page:
 *  1. An explicit owner override (businessConfig.brandColor) always wins.
 *  2. Otherwise, sample the business logo for its dominant vibrant hue.
 *  3. Otherwise (no logo, or sampling fails), use a fixed per-category color
 *     so the page is never stuck on a flat platform-purple default.
 *
 * The category color is returned synchronously on first render so there's
 * no flash of "wrong" color while the (async) logo sampling resolves.
 */
export function useBusinessBrandColor(
  logoUrl: string | null | undefined,
  listingType: string | null | undefined,
  overrideHex?: string | null
): UseBusinessBrandColorResult {
  const categoryDefault = useMemo(() => getCategoryAccent(listingType), [listingType]);
  const [derivedHex, setDerivedHex] = useState<string | null>(null);

  useEffect(() => {
    setDerivedHex(null);
    if (overrideHex || !logoUrl) return;
    let cancelled = false;
    extractDominantColor(logoUrl).then((hex) => {
      if (!cancelled && hex) setDerivedHex(hex);
    });
    return () => {
      cancelled = true;
    };
  }, [logoUrl, overrideHex]);

  const accent = overrideHex ?? derivedHex ?? categoryDefault;

  return useMemo(
    () => ({
      accent,
      accentDark: darkenHex(accent),
      tint: hexToRgba(accent, 0.12),
      isDerived: !!overrideHex || !!derivedHex,
    }),
    [accent, overrideHex, derivedHex]
  );
}
